import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { startOfWeek, subWeeks } from 'date-fns'
import { type JSX } from 'react'
import { DateRangePicker } from '~/components/date-picker'
import {
  ChannelsCommandList,
  CommandPopover,
  GroupsCommandList,
  ScorecardsCommandList,
  UsersCommandList,
  WorkspacesCommandList,
} from '~/features/command-lists'
import { usePermission } from '~/features/access-control'
import { useFilterAccess } from '~/features/filter-control'
import { LinkButton } from '~/features/navigation'
import { useTimespanPreference, useTitle, useWeekStartsOn } from '~/hooks'
import { useTRPC } from '~/services/trpc'

import { FilterCollection } from '@hap/analytics'
import {
  Button,
  Heading1,
  Heading2,
  Icon,
  Skeleton,
  Switch,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@hap/components'
import { Timespan } from '@hap/utils'

import { SearchParams, searchParamsSchema } from '..'
import { useAnalyticsPresets } from '../-components/hooks'
import {
  hasEditableMetricFilters,
  preserveReadOnlyMetricFilters,
} from '../-components/metric-filter-access'
import { MetricFilters } from '../-components/metric-widget'
import { AgentLeaderBoard, ScorecardLeaderBoard } from './-component/leaderboards'
import { AgentsBelowGoal } from './-component/metrics'
import { ScoreBreakdown } from './-component/score-breakdown'
import { ScorecardCriteriaTrends } from './-component/scorecard-criteria-trends'

export const Route = createFileRoute(
  '/_authenticated/$alias/_app/indicators/$indicatorId/',
)({
  component: RouteComponent,
  validateSearch: searchParamsSchema,
})

const className = 'text-lg'

export function RouteComponent(): JSX.Element {
  const canViewLeaderboard = usePermission({
    resource: 'leaderboard',
    actions: ['view_all'],
  })
  const { isFilterAllowed } = useFilterAccess()
  const trpc = useTRPC()
  const { indicatorId } = Route.useParams()
  const { filters, timespan: selectedTimespan } = Route.useSearch()
  const weekStartsOn = useWeekStartsOn()
  const { timespan, setTimespanPreference } = useTimespanPreference({
    selectedTimespan,
    startOffset: (date) => startOfWeek(subWeeks(date, 3), { weekStartsOn }),
  })
  const { data } = useQuery(trpc.scorecardCriteria.getById.queryOptions(indicatorId))
  const navigate = useNavigate({ from: Route.fullPath })

  useTitle(data?.name || 'Loading Metric')

  const daterange = Timespan.toStaticTimespan(timespan, weekStartsOn)

  const params = useAnalyticsPresets({
    filters: filters,
    criteriaId: indicatorId,
    timespan: daterange,
  })

  return (
    <div className="gutters my-10 space-y-4">
      <header className="flex gap-2">
        <LinkButton
          to="/$alias/indicators"
          from={Route.fullPath}
          search={{ filters, timespan }}
          variant="ghost"
          size="icon"
        >
          <Icon name="arrow-left" />
        </LinkButton>
        <Heading1 className="shrink grow">
          <Skeleton
            loading={data?.name === undefined}
            className="inline-block w-full max-w-[260px]"
            asChild
          >
            {data?.name}
          </Skeleton>
        </Heading1>
      </header>

      <div className="flex flex-wrap items-center gap-2">
        {hasEditableMetricFilters(filters, isFilterAllowed) && (
          <Button
            variant="ghost"
            onClick={() =>
              navigate({
                search: (prev: SearchParams) => ({
                  ...prev,
                  filters: preserveReadOnlyMetricFilters(prev.filters, isFilterAllowed),
                }),
              })
            }
            icon="arrow-counter-clockwise"
          >
            Reset
          </Button>
        )}

        <DateRangePicker
          value={timespan}
          onValueChange={(timespan) => {
            setTimespanPreference(timespan)
            navigate({
              search: (prev: SearchParams) => ({ ...prev, timespan }),
            })
          }}
        />

        <CommandPopover
          filterField="workspaceId"
          label="Workspaces"
          collection={FilterCollection.Workspaces}
          selected={filters.workspaceIds?.values || []}
        >
          <WorkspacesCommandList
            multiselect
            onSelect={(ids) => handleFilterValueChange('workspaceIds', ids)}
          />
        </CommandPopover>

        <CommandPopover
          filterField="channelId"
          collection={FilterCollection.Channels}
          label="Channels"
          selected={filters.channelIds?.values || []}
        >
          <ChannelsCommandList
            multiselect
            onSelect={(ids) => handleFilterValueChange('channelIds', ids)}
          />
        </CommandPopover>

        <CommandPopover
          filterField="scorecardId"
          collection={FilterCollection.Scorecards}
          label="Scorecards"
          selected={filters.scorecardIds?.values || []}
        >
          <ScorecardsCommandList
            multiselect
            onSelect={(ids) => handleFilterValueChange('scorecardIds', ids)}
            contextFilters={{
              workspaceId: {
                $eq: filters.workspaceIds?.values || [],
              },
            }}
          />
        </CommandPopover>
        <CommandPopover
          filterField="groupIds"
          collection={FilterCollection.Groups}
          label="Groups"
          selected={filters.groupIds?.values || []}
        >
          <GroupsCommandList
            multiselect
            onSelect={(ids) => handleFilterValueChange('groupIds', ids)}
          />
        </CommandPopover>
        <CommandPopover
          filterField="assignedSupervisorId"
          collection={FilterCollection.Users}
          label="Reports To"
          selected={filters.reportToIds?.values || []}
        >
          <UsersCommandList
            multiselect
            onSelect={(ids) => handleFilterValueChange('reportToIds', ids)}
            contextFilters={{
              subordinates: {
                $exists: true,
              },
            }}
          />
        </CommandPopover>
        <CommandPopover
          filterField="assignedTo"
          collection={FilterCollection.Users}
          label="Members"
          selected={filters.assignedTo?.values || []}
        >
          <UsersCommandList
            multiselect
            onSelect={(ids) => handleFilterValueChange('assignedTo', ids)}
          />
        </CommandPopover>
        {(isFilterAllowed('reviewedAt') || !!filters.reviewed) && (
          <label className="flex items-center gap-2">
            <Switch
              checked={!!filters.reviewed}
              disabled={!isFilterAllowed('reviewedAt')}
              onCheckedChange={(checked: boolean) =>
                handleFilterValueChange('reviewed', checked)
              }
            />
            <div>Show Reviewed</div>
          </label>
        )}
      </div>

      <div className="grid grid-cols-3 gap-3">
        <AgentsBelowGoal
          filters={filters}
          criteriaId={indicatorId}
          timespan={params.timespan as Timespan.StaticTimespan}
        />
      </div>
      <section className="">
        <ScorecardCriteriaTrends
          criteriaId={indicatorId}
          filters={filters}
          timespan={params.timespan as Timespan.StaticTimespan}
        />
      </section>

      {!!data?.scale && (
        <section className="bg-accent grid gap-4 rounded-md p-4">
          <Heading2>Score split</Heading2>
          <ScoreBreakdown {...params} scale={data.scale} />
        </section>
      )}

      <Tabs defaultValue={canViewLeaderboard ? 'leaderboard' : 'scorecards'}>
        <TabsList>
          {canViewLeaderboard && (
            <TabsTrigger value="leaderboard" className={className}>
              Leaderboard
            </TabsTrigger>
          )}
          <TabsTrigger value="scorecards" className={className}>
            Scorecards
          </TabsTrigger>
        </TabsList>
        {canViewLeaderboard && (
          <TabsContent value="leaderboard">
            <AgentLeaderBoard
              timespan={params.timespan}
              filters={[
                ...params.filters,
                {
                  field: 'id',
                  op: 'isNotNull',
                  dataset: 'evaluations',
                },
              ]}
            />
          </TabsContent>
        )}
        <TabsContent value="scorecards">
          <ScorecardLeaderBoard
            orderDirection="desc"
            timespan={params.timespan as Timespan.StaticTimespan}
            filters={[
              ...params.filters,
              {
                field: 'scorecardId',
                op: 'isNotNull',
                dataset: 'evaluationCriteriaScores',
              },
            ]}
          />
        </TabsContent>
      </Tabs>
    </div>
  )

  function handleFilterValueChange(field: keyof MetricFilters, ids: string[] | boolean) {
    navigate({
      search: (prev: SearchParams) => {
        function compileFilters() {
          if (typeof ids === 'boolean') {
            if (ids === true) {
              return {
                ...prev.filters,
                [field]: { op: 'isNotNull' },
              }
            } else {
              return Object.fromEntries(
                Object.entries(prev).filter(([key]) => key !== field),
              )
            }
          }

          if (prev.filters[field]?.op === 'isNotNull') {
            console.error('this should not happen')
            return {
              ...prev.filters,
            }
          }

          return {
            ...prev.filters,
            [field]: ids.length > 0 ? { op: 'in', values: ids } : undefined,
          }
        }

        return {
          ...prev,
          filters: compileFilters(),
        }
      },
    })
  }
}
